import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import TelegramIcon from '@mui/icons-material/Telegram';
import RedditIcon from '@mui/icons-material/Reddit';
import PinterestIcon from '@mui/icons-material/Pinterest';
import GroupsIcon from '@mui/icons-material/Groups';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ChatIcon from '@mui/icons-material/Chat';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PublicIcon from '@mui/icons-material/Public';
import { Avatar, Box } from '@mui/material';
import { useState } from 'react';

// Source of truth for every connectable / displayable platform.
// `slug` maps to the Simple Icons brand logo (real network logos), with the
// MUI `Icon` kept as an offline fallback.
const MAP = {
  facebook: { Icon: FacebookIcon, color: '#1877F2', label: 'Facebook', slug: 'facebook' },
  facebook_page: { Icon: FacebookIcon, color: '#1877F2', label: 'Facebook Page', slug: 'facebook' },
  facebook_group: { Icon: GroupsIcon, color: '#1877F2', label: 'Facebook Group', slug: 'facebook' },
  instagram: { Icon: InstagramIcon, color: '#E1306C', label: 'Instagram', slug: 'instagram' },
  instagram_business: { Icon: InstagramIcon, color: '#E1306C', label: 'Instagram Business', slug: 'instagram' },
  linkedin: { Icon: LinkedInIcon, color: '#0A66C2', label: 'LinkedIn', slug: 'linkedin' },
  twitter: { Icon: TwitterIcon, color: '#0F1419', label: 'Twitter / X', slug: 'x' },
  x: { Icon: TwitterIcon, color: '#0F1419', label: 'X', slug: 'x' },
  // Icon is the real mark, not a tintable glyph — see YouTubeBrandIcon.
  // #FF0033 is YouTube's red, measured from their own artwork — not the
  // #FF0000 everyone reproduces from memory. Used for borders and shadows;
  // the mark itself is their file, never a colour we apply.
  youtube: { Icon: YouTubeBrandIcon, color: '#FF0033', label: 'YouTube', slug: 'youtube' },
  telegram: { Icon: TelegramIcon, color: '#229ED9', label: 'Telegram', slug: 'telegram' },
  tiktok: { Icon: MusicNoteIcon, color: '#010101', label: 'TikTok', slug: 'tiktok' },
  threads: { Icon: AlternateEmailIcon, color: '#000000', label: 'Threads', slug: 'threads' },
  pinterest: { Icon: PinterestIcon, color: '#E60023', label: 'Pinterest', slug: 'pinterest' },
  reddit: { Icon: RedditIcon, color: '#FF4500', label: 'Reddit', slug: 'reddit' },
  discord: { Icon: ChatIcon, color: '#5865F2', label: 'Discord', slug: 'discord' },
  snapchat: { Icon: PhotoCameraIcon, color: '#FFFC00', label: 'Snapchat', slug: 'snapchat' },
  google: { Icon: StorefrontIcon, color: '#4285F4', label: 'Google Business', slug: 'google' },
  google_business: { Icon: StorefrontIcon, color: '#4285F4', label: 'Google Business Profile', slug: 'google' },
  gmb: { Icon: StorefrontIcon, color: '#4285F4', label: 'Google Business', slug: 'google' },
};

export function getPlatformMeta(platform) {
  const key = String(platform || '').toLowerCase();
  return MAP[key] || { Icon: PublicIcon, color: '#6B7280', label: platform || 'Platform' };
}

/**
 * Pick a legible glyph colour (black/white) against a brand background.
 *
 * Exported because callers that paint a brand-coloured surface THEMSELVES need
 * the same answer — see NetworkGrid, whose tiles fill with `meta.color` on
 * hover and have to know which way to flip the logo on top of it.
 */
export function glyphColorFor(hex) {
  const h = String(hex || '#666').replace('#', '');
  if (h.length < 6) return 'ffffff';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.7 ? '000000' : 'ffffff';
}


/**
 * YouTube's minimum icon height, in px.
 *
 * "The YouTube icon and logo ... height should never be smaller than 20dp"
 * (developers.google.com/youtube/terms/branding-guidelines). A YouTube API
 * Services review measured ours at 9.91 and failed the client on it.
 */
export const YT_MIN_HEIGHT = 20;

/**
 * The mark's own proportions, measured from YouTube's own artwork: 827 x 579,
 * so 1.4283 rather than the 1.4 a hand-drawn version lands on.
 */
const YT_RATIO = 827 / 579;

/*
 * The exclusion zone, as a multiple of the icon's height.
 *
 * brand.youtube/youtube-icon publishes this as a diagram rather than a number:
 * the icon sits inside a dotted boundary with a play triangle in each margin,
 * the triangle being the unit. Measuring that diagram gives a margin of 88.5px
 * horizontally and 104px vertically around a 241px-tall icon -- the triangle's
 * own width and height.
 *
 * Kept as the measured fractions rather than rounded, because the whole reason
 * this file is on its third attempt is that round numbers were close enough to
 * look right and not close enough to pass.
 */
const YT_CLEAR_X = 88.5 / 241;
const YT_CLEAR_Y = 104 / 241;

/**
 * The YouTube icon — YouTube's own file, not a redrawing of it.
 *
 * This started as the Simple Icons silhouette, tinted and dropped inside a
 * coloured circle. That was rejected, so it became an inline SVG of the
 * play button. That was rejected too, and measuring the artwork Google sent
 * back shows why: the red is #FF0033, not the #FF0000 everyone reproduces
 * from memory, and the proportions are 1.4283, not 1.4.
 *
 * So this no longer redraws anything. It is the file Google supplied, cropped
 * to the mark and otherwise untouched, inside the clear space the guidelines
 * require: right red, right shape, right proportions, right breathing room, by
 * construction rather than by eye.
 *
 * `height` is a floor, not a setting: a caller asking for 14 gets 20, because
 * no caller is allowed to render it smaller than the guidelines permit.
 */
export function YouTubeMark({ height = YT_MIN_HEIGHT, title = 'YouTube' }) {
  const h = Math.max(Number(height) || 0, YT_MIN_HEIGHT);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexShrink: 0,
        px: `${h * YT_CLEAR_X}px`,
        py: `${h * YT_CLEAR_Y}px`,
      }}
    >
      <Box
        component="img"
        src="/brand/youtube-icon.png"
        alt={title}
        sx={{ height: h, width: h * YT_RATIO, display: 'block', objectFit: 'contain' }}
      />
    </Box>
  );
}


/**
 * A drop-in for the MUI YouTube icon that is actually YouTube's icon.
 *
 * Roughly a dozen places render `meta.Icon` directly -- chip rows, failure
 * dialogs, previews -- and each one styled a monochrome glyph with `sx`. Every
 * one of those was a branding violation, so rather than patch each call site
 * the registry hands them this instead. It reads the size the caller asked for
 * and ignores the colour they asked for, because the colour is not ours to
 * choose.
 */
function YouTubeBrandIcon({ sx, fontSize }) {
  const asked = sx?.fontSize ?? fontSize;

  return <YouTubeMark height={typeof asked === 'number' ? asked : YT_MIN_HEIGHT} />;
}
/** Is this platform key YouTube? Call sites use it to opt out of tinting. */
export function isYouTube(platform) {
  return String(platform || '').toLowerCase() === 'youtube';
}
/**
 * The real social-network logo as an <img> (Simple Icons CDN), tinted to sit on
 * a brand-colored background. Falls back to the MUI brand icon if the image
 * can't load (e.g. offline).
 */
export function PlatformGlyph({ platform, size = 20, tone }) {
  // Hook first: the YouTube branch below is an early return, and a hook after
  // it would only run for some platforms.
  const [failed, setFailed] = useState(false);

  // Never tinted, never below the floor — see YouTubeMark.
  if (isYouTube(platform)) return <YouTubeMark height={Math.max(size, YT_MIN_HEIGHT)} />;

  const meta = getPlatformMeta(platform);
  const glyph = tone || glyphColorFor(meta.color);
  if (meta.slug && !failed) {
    return (
      <Box
        component="img"
        src={`https://cdn.simpleicons.org/${meta.slug}/${glyph}`}
        alt={meta.label}
        onError={() => setFailed(true)}
        sx={{ width: size, height: size, objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
      />
    );
  }
  const Icon = meta.Icon;
  return <Icon sx={{ fontSize: size, color: `#${glyph}` }} />;
}

export const CONNECTABLE_PLATFORMS = [
  'facebook',
  'instagram',
  'tiktok',
  'x',
  'youtube',
  'telegram',
  'threads',
  'pinterest',
  'reddit',
  'discord',
  'snapchat',
  'linkedin',
  // Its own tile at last. It shared one with YouTube — one Google connection
  // covering both products — so there was no way to connect a Business Profile
  // without also handing over the YouTube scopes.
  'google_business',
];

/**
 * PlatformIcon
 * - withAvatar: round colored avatar
 * - badge: render a tiny platform badge in the bottom-right (used over account avatars)
 */
export default function PlatformIcon({ platform, withAvatar = false, size = 24, sx }) {
  const { Icon, color } = getPlatformMeta(platform);
  if (withAvatar) {
    return (
      <Avatar sx={{ bgcolor: color + '1A', color, width: 36, height: 36, ...sx }}>
        <Icon sx={{ fontSize: 20 }} />
      </Avatar>
    );
  }
  return <Icon sx={{ color, fontSize: size, ...sx }} />;
}

/** A small circular brand badge (solid color, real network logo) for overlaying on avatars. */
export function PlatformBadge({ platform, size = 16, ring = '#fff' }) {
  const { color } = getPlatformMeta(platform);

  /*
   * YouTube is not badged.
   *
   * Every other network here is a single-colour silhouette on a brand-colour
   * disc, which reads fine. Doing that to YouTube recolours its mark and puts
   * it in a shape that is not its own, and shrinks it to 60% of a badge that
   * is already smaller than the 20dp floor. So YouTube gets its real icon, at
   * its real proportions, at no less than the minimum height.
   */
  if (isYouTube(platform)) {
    return <YouTubeMark height={Math.max(size, YT_MIN_HEIGHT)} />;
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `2px solid ${ring}`,
        boxSizing: 'content-box',
      }}
    >
      <PlatformGlyph platform={platform} size={size * 0.6} />
    </Box>
  );
}

/** Account avatar with a platform badge in the bottom-right corner. */
export function AccountAvatar({ platform, name, src, size = 40, sx }) {
  const initial = String(name || '?').charAt(0).toUpperCase();

  /*
   * YouTube sits BESIDE the avatar, not on it.
   *
   * The badge overlay works because every other network is a small glyph on a
   * disc that can shrink to a corner. YouTube's mark has a floor of 20dp and a
   * clear space around it, which together are wider than the avatar it was
   * meant to sit on -- so it covered the photo instead of badging it, and an
   * avatar inside the exclusion zone is exactly what that zone forbids.
   */
  if (isYouTube(platform)) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, ...sx }}>
        <Avatar src={src} sx={{ width: size, height: size, bgcolor: 'primary.main', fontWeight: 700 }}>
          {initial}
        </Avatar>
        <YouTubeMark height={YT_MIN_HEIGHT} />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0, ...sx }}>
      <Avatar
        src={src}
        sx={{ width: size, height: size, bgcolor: 'primary.main', fontWeight: 700 }}
      >
        {initial}
      </Avatar>
      <Box sx={{ position: 'absolute', right: -2, bottom: -2 }}>
        <PlatformBadge platform={platform} size={size * 0.42} />
      </Box>
    </Box>
  );
}
